import { useMemo } from 'react';
import { Navigate, useParams, Link } from 'react-router-dom';
import { getCategory } from '../data/categories';
import { getProductsByCategory, getGroupsByCategory } from '../data/products';
import { dyes } from '../data/dyes';
import { company } from '../data/company';
import { downloadFile } from '../utils/utils';
import { useReveal } from '../hooks/useReveal';
import PageHero from '../components/PageHero';
import ProductCard from '../components/ProductCard';
import Breadcrumbs from '../components/Breadcrumbs';
import CTASection from '../components/CTASection';
import Icon from '../components/Icon';
import machiningOil from '../assets/images/machining-oil.jpg';
import cncMachining from '../assets/images/cnc-machining.jpg';

// Only families with a relevant photo get one; others fall back to the brand gradient.
const categoryHeroImage: Record<string, string> = {
  'petroleum-sulphonates': machiningOil,
  'metalworking-fluids': cncMachining,
};

export default function CategoryPage() {
  const { categorySlug = '' } = useParams();
  const category = getCategory(categorySlug);
  useReveal(categorySlug);

  const groups = useMemo(() => getGroupsByCategory(categorySlug), [categorySlug]);
  const products = useMemo(() => getProductsByCategory(categorySlug), [categorySlug]);

  if (!category) return <Navigate to="/products" replace />;

  const isDyes = category.slug === 'fuel-petroleum-dyes';

  return (
    <div>
      <PageHero
        eyebrow="Product Family"
        title={category.name}
        description={category.intro}
        image={categoryHeroImage[category.slug]}
      >
        <Breadcrumbs
          items={[
            { label: 'Home', to: '/' },
            { label: 'Products', to: '/products' },
            { label: category.name },
          ]}
        />
      </PageHero>

      {/* Application tags + download */}
      <section className="border-b border-ink/5 bg-surface">
        <div className="container-page flex flex-wrap items-center gap-3 py-6">
          <span className="text-sm font-semibold text-text-primary">Key applications:</span>
          {category.applications.map((a) => (
            <span key={a} className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-ink-soft">
              {a}
            </span>
          ))}
          <button
            type="button"
            onClick={() => downloadFile(company.productGuide.fileUrl, company.productGuide.fileName)}
            className="btn-outline ml-auto py-2"
          >
            <Icon name="download" className="h-4 w-4" /> Product Guide
          </button>
        </div>
      </section>

      {isDyes ? (
        <DyeRange />
      ) : (
        <section className="container-page py-16 sm:py-24">
          {groups.map((group, gi) => {
            const groupProducts = products.filter((p) => p.group === group);
            return (
              <div key={group} className="reveal mb-16 last:mb-0">
                <div className="mb-8 flex items-baseline gap-4 border-b border-ink/15 pb-4">
                  <span className="text-2xl text-secondary/60">{String(gi + 1).padStart(2, '0')}</span>
                  <h2 className="text-2xl font-semibold text-text-primary">{group}</h2>
                  <span className="ml-auto text-xs uppercase tracking-wider text-text-secondary">
                    {groupProducts.length} {groupProducts.length === 1 ? 'product' : 'products'}
                  </span>
                </div>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {groupProducts.map((p) => (
                    <ProductCard key={p.id} product={p} />
                  ))}
                </div>
              </div>
            );
          })}
        </section>
      )}

      <CTASection />
    </div>
  );
}

function DyeRange() {
  return (
    <section className="container-page py-16 sm:py-20">
      <div className="reveal">
        <h2 className="text-xl font-bold text-text-primary">Solvent-Soluble Dye Range</h2>
        <p className="mt-2 max-w-3xl text-text-secondary">
          Formulated for non-aqueous solvents, oils and petroleum-based products, ensuring vibrant
          coloration and stability for industrial and automotive applications.
        </p>
      </div>

      {/* Colour swatches */}
      <div className="reveal mt-8 grid grid-cols-3 gap-3 sm:grid-cols-5">
        {dyes.map((d) => (
          <div key={d.name} className="card flex flex-col items-center p-4 text-center">
            <span
              className="h-10 w-10 rounded-full ring-2 ring-white shadow"
              style={{ backgroundColor: d.color }}
            />
            <span className="mt-3 text-xs font-semibold leading-tight text-text-primary">{d.name}</span>
          </div>
        ))}
      </div>

      {/* Full table */}
      <div className="reveal mt-10 overflow-hidden rounded-xl ring-1 ring-ink/10">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="bg-primary text-xs uppercase tracking-wide text-white">
                <th className="px-5 py-3 font-semibold">Dye Name</th>
                <th className="px-5 py-3 font-semibold">Applications</th>
                <th className="px-5 py-3 font-semibold">Purpose</th>
              </tr>
            </thead>
            <tbody>
              {dyes.map((d, i) => (
                <tr key={d.name} className={i % 2 ? 'bg-background/60' : 'bg-surface'}>
                  <td className="px-5 py-3">
                    <span className="flex items-center gap-2 font-medium text-text-primary">
                      <span
                        className="h-3 w-3 flex-none rounded-full ring-1 ring-ink/10"
                        style={{ backgroundColor: d.color }}
                      />
                      {d.name}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-text-secondary">{d.applications}</td>
                  <td className="px-5 py-3 text-text-secondary">{d.purpose}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="mt-6 text-sm text-text-secondary">
        Looking for a specific shade or custom dye?{' '}
        <Link to="/contact" className="font-semibold text-primary hover:text-primary-dark">
          Talk to our team
        </Link>
        .
      </p>
    </section>
  );
}
